/**
 * User CRDT Bridge
 *
 * Coordinates User CRDT sync for vault index and preferences.
 * This module provides the interface between VaultStorage and the User
 * Yjs document(s).
 *
 * Phase 5c — dual-instance support:
 *
 *   Two slots coexist: `_local` and `_online`. They are independent
 *   Y.Docs with their own IndexedDB / filesystem persistence and (for
 *   `_online` only) their own WebSocket connection to `/ws/user`.
 *
 *     - `_local` holds entries for local-only docs (no DEK, no sync).
 *       Always present in Tauri; never present in the web build.
 *
 *     - `_online` holds entries for shared docs (with wrappedDEK, role,
 *       and a real `/ws/user` connection). Present whenever a real
 *       user is signed in.
 *
 *   The legacy "singleton" API surface (`setDocEntry`, `getDocEntry`,
 *   etc.) is preserved as backward-compat shims that route via
 *   `_crdtForDoc(id)`. For docs that don't yet exist (e.g., during
 *   creation) the caller passes `storage: "local" | "online"` so the
 *   right slot is chosen.
 *
 *   Notifications and remote-update events come from `_online` only —
 *   `/ws/user` doesn't exist for local data.
 */

import { UserYjsDocManager } from "./user-yjs-doc.js";
import { UserSyncManager, UserConnectionState } from "./user-websocket.js";
import { emit, Events } from "../core/events.js";
import * as StorageHealth from "./storage-health.js";

// ============================================
// Module state — two independent slots
// ============================================

/** @type {{ docManager, userId } | null} */
let _local = null;

/** @type {{ docManager, syncManager, userId, hasMigrated } | null} */
let _online = null;

// ============================================
// Slot helpers
// ============================================

/**
 * Look up which slot owns a given docId.
 * Returns the slot or null if neither has it.
 *
 * The order (online first) matches the common case: when a user is
 * signed in, most operations are on shared docs.
 *
 * @param {string} docId
 */
function _crdtForDoc(docId) {
  if (_online?.docManager.getDocEntry(docId)) return _online;
  if (_local?.docManager.getDocEntry(docId)) return _local;
  return null;
}

/**
 * Get a slot by storage kind ("local" or "online").
 * Returns null if that slot isn't initialized.
 *
 * @param {"local" | "online"} storage
 */
function _slotFor(storage) {
  if (storage === "local") return _local;
  if (storage === "online") return _online;
  return null;
}

/**
 * Pick the active "default" slot for callers that don't specify
 * storage. Online wins when both are present (signed-in users edit
 * shared docs more often than local ones), local otherwise.
 */
function _activeSlot() {
  return _online || _local;
}

/**
 * Strip transient in-memory annotations (anything keyed with a
 * leading `_`) before persisting to the underlying Y.Map. The
 * vault-storage refresh tags entries with `_storage` for routing;
 * future annotations should never leak to the CRDT either.
 */
function _stripTransient(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (!k.startsWith("_")) out[k] = v;
  }
  return out;
}

// ============================================
// Lifecycle
// ============================================

/**
 * Initialize a slot.
 *
 * The `storage` option (or, for backward compat, a `local-` prefix on
 * the userId) decides which slot is initialized.
 *
 * @param {string} uid
 * @param {CryptoKey} masterKey
 * @param {Object} options
 * @param {"local"|"online"} [options.storage]
 * @param {boolean} [options.isNewUser]
 * @param {boolean} [options.skipSync] — applies to "online" only
 * @returns {Promise<{ initialized: true }>}
 */
export async function init(uid, masterKey, options = {}) {
  const storage =
    options.storage ||
    (typeof uid === "string" && uid.startsWith("local-") ? "local" : "online");

  if (storage === "local") {
    await initLocal(uid);
  } else {
    await initOnline(uid, masterKey, options);
  }
  return { initialized: true };
}

/**
 * Initialize the local slot. No server, no sync, no encryption key —
 * the local CRDT is just a Yjs doc backed by file/IDB persistence.
 *
 * @param {string} uid - Local user ID (typically `local-<uuid>`)
 */
export async function initLocal(uid) {
  if (_local) {
    console.warn("[UserCRDT] Local already initialized, destroying first");
    await destroyLocal();
  }

  console.log("[UserCRDT] Initializing local slot for:", uid);

  const docManager = new UserYjsDocManager(uid, {
    onUpdate: () => handleRemoteUpdate("local"),
  });
  await docManager.initPersistence();

  _local = { docManager, userId: uid };
  console.log("[UserCRDT] Local slot ready");
}

/**
 * Initialize the online slot. Connects to `/ws/user` (lazily, after
 * `startSync`) and uses the master key for envelope encryption.
 *
 * @param {string} uid - Real (server-issued) user ID
 * @param {CryptoKey} masterKey
 * @param {Object} options
 */
export async function initOnline(uid, masterKey, options = {}) {
  const { isNewUser = false } = options;

  if (_online) {
    console.warn("[UserCRDT] Online already initialized, destroying first");
    await destroyOnline();
  }

  console.log("[UserCRDT] Initializing online slot for:", uid, { isNewUser });

  const docManager = new UserYjsDocManager(uid, {
    onUpdate: () => handleRemoteUpdate("online"),
  });
  await docManager.initPersistence();

  // Online structure initialization belongs to the completed sync handshake.
  // A separate signup confirmation cannot serialize concurrent clients.

  const syncManager = new UserSyncManager({
    onStateChange: handleStateChange,
    onError: options.onError || handleError,
    onRemoteUpdate: options.onRemoteUpdate || null,
    onNotification: handleNotification,
    onNotificationDismissed: handleNotificationDismissed,
  });

  syncManager.setEncryptionKey(masterKey);

  _online = { docManager, syncManager, userId: uid, hasMigrated: false };

  console.log("[UserCRDT] Online slot ready");
}

/**
 * Start WebSocket sync on the online slot. No-op if online isn't
 * initialized — local-only sessions never need this.
 */
export async function startSync(timeout = 10000) {
  if (!_online) {
    console.warn("[UserCRDT] No online slot, cannot start sync");
    return { synced: false, hasData: false };
  }

  try {
    console.log("[UserCRDT] Starting WebSocket sync (online slot)...");
    await _online.syncManager.connect(_online.userId, _online.docManager);

    let synced = false;
    const wsState = _online.syncManager.getState();
    if (wsState === "synced") {
      synced = true;
    } else if (wsState === "connected" || wsState === "syncing") {
      synced = await _online.syncManager.waitForSync(timeout);
    } else {
      console.log("[UserCRDT] WebSocket not connected, skipping sync wait");
    }

    const hasOnlineData = _online.docManager.hasData();
    console.log(
      `[UserCRDT] Sync complete: synced=${synced}, hasData=${hasOnlineData}, docs=${_online.docManager.docs?.size || 0}`,
    );
    return { synced, hasData: hasOnlineData };
  } catch (err) {
    console.error("[UserCRDT] Failed to start sync:", err);
    return { synced: false, hasData: false };
  }
}

export async function stopSync() {
  if (_online?.syncManager) {
    await _online.syncManager.disconnect();
    console.log("[UserCRDT] Online sync stopped");
  }
}

/**
 * Destroy both slots. Used at logout / app teardown when the user
 * leaves the app entirely; for sign-out alone, prefer
 * `destroyOnline()` which keeps the local CRDT alive.
 */
export async function destroy() {
  await destroyOnline();
  await destroyLocal();
}

export async function destroyOnline() {
  if (!_online) return;
  if (_online.syncManager) {
    await _online.syncManager.disconnect();
  }
  if (_online.docManager) {
    await _online.docManager.destroy();
  }
  _online = null;
  console.log("[UserCRDT] Online slot destroyed");
}

export async function destroyLocal() {
  if (!_local) return;
  if (_local.docManager) {
    await _local.docManager.destroy();
  }
  _local = null;
  console.log("[UserCRDT] Local slot destroyed");
}

// ============================================
// State queries
// ============================================

/** Any slot initialized? */
export function isInitialized() {
  return _local !== null || _online !== null;
}

/** Alias for compatibility (calendar.js etc.). */
export { isInitialized as isEnabled };

/** Is the online slot initialized? */
export function isOnlineEnabled() {
  return _online !== null;
}

/** Is the local slot initialized? */
export function isLocalEnabled() {
  return _local !== null;
}

/**
 * Does any slot have data?
 *
 * Defaults to checking the active slot for backward compat. Callers
 * that want a specific slot use `hasOnlineData()` / `hasLocalData()`.
 */
export function hasData() {
  return _activeSlot()?.docManager.hasData() || false;
}

export function hasOnlineData() {
  return _online?.docManager.hasData() || false;
}

export function hasLocalData() {
  return _local?.docManager.hasData() || false;
}

/**
 * Initialize the vault index structure on a specific slot.
 * Defaults to active slot for backward compat.
 *
 * @param {string} reason
 * @param {"local"|"online"} [storage]
 */
export function initStructure(reason = "logged-out-mode", storage) {
  const slot = storage ? _slotFor(storage) : _activeSlot();
  if (!slot) {
    console.warn("[UserCRDT] No slot to init structure on");
    return;
  }
  StorageHealth.assertHealthy();
  slot.docManager._initVaultIndexStructure(reason);
}

export function hasPendingWrites() { return !!_online?.syncManager?.hasPendingWrites(); }

/** Sign-out's last sync of the vault (see UserSyncManager.settlePendingWrites). */
export async function settlePendingWrites(timeoutMs) {
  return _online?.syncManager ? _online.syncManager.settlePendingWrites(timeoutMs) : true;
}

/** The account vault's document id (its dirty bit, unsynced-changes.js). */
export function getOnlineVaultDocId() {
  return _online?.docManager?.docId || null;
}

export function getConnectionState() {
  return _online?.syncManager?.getState() || UserConnectionState.DISCONNECTED;
}

export async function retryPausedSync() {
  if (!_online?.syncManager) throw new Error('Vault sync is unavailable');
  const manager = _online.syncManager;
  if (manager.ws?.readyState === WebSocket.OPEN) manager._sendSyncStep1();
  else await manager.connect(_online.userId, _online.docManager);
  return manager.waitForSync(10000);
}

export function triggerReconnect() {
  if (_online?.syncManager) {
    _online.syncManager.triggerReconnect();
  }
}

/** Whether the server refused the vault's connection: too many for this account. */
export function isRefusedTooMany() {
  return !!_online?.syncManager?.isRefusedTooMany?.();
}

/**
 * Probe the user-CRDT connection after a tab-resume signal. See
 * crdt-bridge.js#probeConnection for rationale.
 */
export function probeConnection() {
  if (_online?.syncManager) {
    _online.syncManager.probeConnection();
  }
}

/**
 * Check if already migrated from legacy
 */
export function wasMigrated() {
  return _online?.hasMigrated || false;
}

// ============================================
// Folder operations
// ============================================
//
// Folder IDs are unique within each slot, and the two slots never
// merge folder structures (per the Phase 5c design — "list local and
// online separately"). So setFolder/deleteFolder need a `storage`
// param. getFolder/getAllFolders accept an optional `storage` and
// default to active slot for backward compat.

/**
 * @param {Object} folder
 * @param {"local"|"online"} [storage]
 */
export function setFolder(folder, storage) {
  const slot = storage ? _slotFor(storage) : _activeSlot();
  if (!slot) {
    console.warn("[UserCRDT] No slot to set folder on");
    return;
  }
  StorageHealth.assertHealthy();
  // L8: strip ALL `_*` transient annotations (see setDocEntry).
  slot.docManager.setFolder(_stripTransient(folder));
}

export function getFolder(folderId, storage) {
  const slot = storage ? _slotFor(storage) : _activeSlot();
  return slot?.docManager.getFolder(folderId) || null;
}

export function getAllFolders(storage) {
  if (storage) {
    return _slotFor(storage)?.docManager.getAllFolders() || [];
  }
  // Backward compat: return active slot's folders only. Dual-aware
  // callers use the explicit storage param.
  return _activeSlot()?.docManager.getAllFolders() || [];
}

export function getAllLocalFolders() {
  return _local?.docManager.getAllFolders() || [];
}

export function getAllOnlineFolders() {
  return _online?.docManager.getAllFolders() || [];
}

export function deleteFolder(folderId, storage) {
  const slot = storage ? _slotFor(storage) : _activeSlot();
  if (!slot) return;
  StorageHealth.assertHealthy();
  slot.docManager.deleteFolder(folderId);
}

// ============================================
// Doc entry operations
// ============================================

/**
 * Add or update a doc entry.
 *
 * Routes by lookup: if the docId exists in either slot, the matching
 * one is used. For new docs (no existing entry), pass an explicit
 * `storage` param.
 *
 * @param {Object} docEntry
 * @param {"local"|"online"} [storage]
 */
export function setDocEntry(docEntry, storage) {
  // Phase 5d follow-up: explicit storage hint wins over lookup. The
  // promote / save-local transitions need to land an entry in a
  // *specific* slot even when the docId already lives in the other
  // slot (e.g. promoteToOnline writes the online entry while the
  // local copy is still there); the previous lookup-first behavior
  // silently re-routed those writes to the slot that already had it.
  // For renames/updates (no storage hint), keep the convenience
  // behavior of finding the slot that owns the doc.
  let slot;
  if (storage) {
    slot = _slotFor(storage);
  } else {
    slot = _crdtForDoc(docEntry.docId);
  }
  if (!slot) {
    // L1: previously fell back to the active slot silently. Surface
    // the fallback so callers that meant to specify storage but
    // forgot are obvious in logs.
    slot = _activeSlot();
    if (slot) {
      console.warn(
        "[UserCRDT] setDocEntry: docId not in any slot and no storage hint — using active slot fallback:",
        docEntry.docId,
      );
    }
  }
  if (!slot) {
    console.warn("[UserCRDT] No slot to set doc entry on:", docEntry.docId);
    return;
  }
  StorageHealth.assertHealthy();
  // L8: strip ALL `_*` transient annotations, not just `_storage`.
  // Future-proofs against new in-memory annotations leaking to the CRDT.
  slot.docManager.setDocEntry(_stripTransient(docEntry));
}

export function getDocEntry(docId) {
  return _crdtForDoc(docId)?.docManager.getDocEntry(docId) || null;
}

/**
 * Returns the storage slot ("local" | "online") that owns the doc,
 * or null if neither does. Used by VaultStorage to decide routing.
 *
 * @param {string} docId
 * @returns {"local" | "online" | null}
 */
export function getLocalMapKey(docId) {
  return _local?.docManager.getLocalMapKey(docId) || null;
}

/** Store a key for a map on this device unless it has one; returns the key in force. */
export function setLocalMapKey(docId, keyBase64) {
  if (!_local) throw new Error("Local vault is not loaded");
  StorageHealth.assertHealthy();
  return _local.docManager.setLocalMapKey(docId, keyBase64);
}

export function getDocStorage(docId) {
  if (_online?.docManager.getDocEntry(docId)) return "online";
  if (_local?.docManager.getDocEntry(docId)) return "local";
  return null;
}

/**
 * Backward compat: returns entries from the active slot only.
 * Dual-aware callers use `getAllLocalDocEntries` /
 * `getAllOnlineDocEntries`.
 */
export function getAllDocEntries() {
  return _activeSlot()?.docManager.getAllDocEntries() || [];
}

export function getAllLocalDocEntries() {
  return _local?.docManager.getAllDocEntries() || [];
}

export function getAllOnlineDocEntries() {
  return _online?.docManager.getAllDocEntries() || [];
}

export function deleteDocEntry(docId) {
  const slot = _crdtForDoc(docId);
  if (!slot) return;
  StorageHealth.assertHealthy();
  slot.docManager.deleteDocEntry(docId);
}

/**
 * Phase 5d: delete a doc entry from a specific slot.
 *
 * Needed for transitions where the doc is transiently present in
 * both slots (e.g. promoteToOnline writes the online entry before
 * removing the local one). The default `deleteDocEntry` resolves
 * the slot via online-first lookup and would delete the wrong copy
 * in that window.
 *
 * No-op if the slot isn't loaded or the entry isn't in it.
 *
 * @param {string} docId
 * @param {"local" | "online"} storage
 */
export function deleteDocEntryFromSlot(docId, storage) {
  const slot = _slotFor(storage);
  if (!slot) return;
  if (!slot.docManager.getDocEntry(docId)) return; // idempotent
  StorageHealth.assertHealthy();
  slot.docManager.deleteDocEntry(docId);
}

export function hasUnifiedDocsMigration() {
  return _online?.docManager.hasUnifiedDocsMigration() || false;
}

export async function runUnifiedDocsMigration(masterKey, keypair) {
  if (!_online) {
    console.warn("[UserCRDT] No online slot, cannot run migration");
    return { migratedDocs: 0, uploadedKeys: 0 };
  }
  StorageHealth.assertHealthy();
  return _online.docManager.migrateToUnifiedDocs(masterKey, keypair);
}

// ============================================
// Keypair
// ============================================
//
// The keypair is logically an online-mode concept (used to wrap DEKs
// for sharing). It's stored on the online slot when present. For
// backward compat with web offline mode — which still generates and
// stores a vestigial keypair on the only slot it has — we fall back
// to the active slot when online isn't initialized.

export function setKeypair(keypair) {
  const slot = _online || _activeSlot();
  if (!slot) {
    console.warn("[UserCRDT] No slot to set keypair on");
    return;
  }
  StorageHealth.assertHealthy();
  slot.docManager.setKeypair(keypair);
}

export function getKeypair() {
  const slot = _online || _activeSlot();
  return slot?.docManager.getKeypair() || null;
}

// ============================================
// Calendar config (lives on whichever slot is active — typically
// online when signed in, local when offline-only)
// ============================================

export function setCalendarConfig(config) {
  const slot = _activeSlot();
  if (!slot) {
    console.warn("[UserCRDT] No slot to set calendar config on");
    return;
  }
  StorageHealth.assertHealthy();
  slot.docManager.setCalendarConfig(config);
}

export function getCalendarConfig() {
  return _activeSlot()?.docManager.getCalendarConfig() || {};
}

// ============================================
// Migration (legacy index → online slot only)
// ============================================

export function migrateFromLegacy(legacyIndex) {
  if (!_online) {
    console.warn("[UserCRDT] No online slot, cannot migrate legacy index");
    return;
  }
  StorageHealth.assertHealthy();

  console.log("[UserCRDT] Migrating from legacy index");
  _online.docManager.fromLegacyIndex(legacyIndex);
  _online.hasMigrated = true;

  emit(Events.USER_CRDT_MIGRATED, { source: "legacy" });
}

export function toLegacyIndex() {
  return _online?.docManager.toLegacyIndex() || null;
}

// ============================================
// Event handlers
// ============================================

function handleRemoteUpdate(source) {
  console.log("[UserCRDT] Update received:", source);
  emit(Events.USER_CRDT_UPDATED, {
    source,
    timestamp: Date.now(),
  });
}

function handleStateChange(newState, oldState) {
  console.log(`[UserCRDT] Online connection state: ${oldState} → ${newState}`);
  emit(Events.USER_CRDT_STATE_CHANGED, {
    state: newState,
    previousState: oldState,
  });
}

function handleError(error) {
  console.error("[UserCRDT] Error:", error);
  emit(Events.USER_CRDT_ERROR, { error });
}

function handleNotification(notification) {
  console.log("[UserCRDT] Notification received:", notification.title);
  emit(Events.USER_NOTIFICATION_RECEIVED, notification);
}

function handleNotificationDismissed(notificationId) {
  console.log("[UserCRDT] Notification dismissed on another device:", notificationId);
  emit(Events.USER_NOTIFICATION_DISMISSED, notificationId);
}

// User CRDT events are defined in engine/core/events.js:
// - USER_CRDT_UPDATED, USER_CRDT_STATE_CHANGED, USER_CRDT_ERROR,
// - USER_CRDT_MIGRATED, USER_NOTIFICATION_RECEIVED, USER_NOTIFICATION_DISMISSED
